// AMLMonthlyDashboard.jsx
import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  collection,
  collectionGroup,
  getDocs,
  onSnapshot,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import DownloadAllUsersMonthlyStatement from "./amlStatement";
import Sidebar from "../../DesignSystem/Sidebar";
import CustomSelect from "../../DesignSystem/Dropdowns/CustomSelect";
import { ReactComponent as SortIcon } from "../../../assets/icons/paraplanningDashboard/Sort.svg";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";

import { dbAML as db } from "../../../backend/firebaseConfig";
import { storage } from "../../../backend/firebaseConfig";
import { getDownloadURL, ref } from "firebase/storage";
import * as XLSX from "xlsx";

import {
  getCheckPrice,
  formatGBP,
  normaliseCheckType,
} from "./Utils/checkPricing";

import "../../../css/Finance/AML/amlDashboard.css";

const RETENTION_MONTH_COUNT = 2;
const SUBCOLLECTION_BATCH_SIZE = 50;

const EXPERIAN_SUBCOLLECTION_NAMES = [
  "ExperianResponse",
  "Experian Response",
  "experianResponse",
  "experian response",
];
const REQUEST_DATA_SUBCOLLECTION_NAMES = [
  "Request Data",
  "RequestData",
  "requestData",
  "request data",
];

const DEFAULT_SORT_CONFIG = {
  key: "adviser",
  direction: "asc",
};

const clean = (value) => (value ?? "").toString().trim();

function getLastNMonths(n = RETENTION_MONTH_COUNT) {
  const now = new Date();
  const months = [];

  for (let index = 0; index < n; index += 1) {
    const date = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - index, 1),
    );

    const value = `${date.getUTCFullYear()}-${String(
      date.getUTCMonth() + 1,
    ).padStart(2, "0")}`;

    const label = date.toLocaleString("en-GB", {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });

    const shortLabel = date.toLocaleString("en-GB", {
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });

    months.push({
      value,
      label,
      shortLabel,
      isCurrentMonth: index === 0,
    });
  }

  return months;
}

function getMonthBounds(yyyyMm) {
  const [year, month] = yyyyMm.split("-").map(Number);

  return {
    start: new Date(Date.UTC(year, month - 1, 1, 0, 0, 0)),
    end: new Date(Date.UTC(year, month, 1, 0, 0, 0)),
  };
}

const nameFromEmail = (email) => {
  const value = clean(email).toLowerCase();

  if (!value || !value.includes("@")) return "";

  return value
    .split("@")[0]
    .replace(/[0-9]+/g, " ")
    .replace(/[._-]+/g, " ")
    .split(" ")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getForAdviserEmailOnly = (record) => {
  const forAdviser = record?.forAdviser;

  if (!forAdviser) return "";

  if (typeof forAdviser === "string") {
    return forAdviser.trim();
  }

  if (typeof forAdviser === "object") {
    return (
      forAdviser.email ||
      forAdviser.adviserEmail ||
      forAdviser.userEmail ||
      forAdviser.workEmail ||
      ""
    );
  }

  return "";
};

const getSummaryEmail = (record) => {
  return getForAdviserEmailOnly(record) || clean(record?.__ownerEmail);
};

const isSimpleOrPepsCheck = (record) => {
  const checkType = clean(record?.checkType).toLowerCase();
  return checkType.includes("simple") || checkType.includes("pep");
};

const hasSessionId = (record) => {
  return (
    record.sessionId !== undefined &&
    record.sessionId !== null &&
    String(record.sessionId).trim() !== ""
  );
};

const hasSubcollection = async (docRef, subcollectionNames) => {
  for (const name of subcollectionNames) {
    try {
      const subcollectionRef = collection(docRef, name);
      const snapshot = await getDocs(query(subcollectionRef, limit(1)));

      if (!snapshot.empty) return true;
    } catch {
      // Try next possible subcollection name.
    }
  }

  return false;
};

const getRequestDataForCheck = async (checkDocRef) => {
  if (!checkDocRef) return {};

  for (const name of REQUEST_DATA_SUBCOLLECTION_NAMES) {
    try {
      const snapshot = await getDocs(query(collection(checkDocRef, name), limit(1)));
      if (!snapshot.empty) return snapshot.docs[0].data() || {};
    } catch {
      // Try the next saved Request Data collection name.
    }
  }

  return {};
};

const getAMLClientName = (record, requestData = {}) => {
  const personal =
    requestData.personal ||
    requestData.requestData?.personal ||
    requestData.input?.personal ||
    {};
  const requestDataName = [
    personal.firstName,
    personal.middleName,
    personal.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    requestDataName ||
    clean(record.clientName) ||
    clean(record.fullName) ||
    clean(record.applicantName) ||
    clean(record.name) ||
    [record.firstName, record.lastName].filter(Boolean).join(" ").trim() ||
    ""
  );
};

export const loadAMLClientName = async (record) =>
  getAMLClientName(record, await getRequestDataForCheck(record?.__ref));

const runInBatches = async (items, batchSize, worker) => {
  const results = [];

  for (let index = 0; index < items.length; index += batchSize) {
    const batch = items.slice(index, index + batchSize);
    const batchResults = await Promise.all(batch.map(worker));
    results.push(...batchResults);
  }

  return results;
};

const shouldIncludeCheck = async (check) => {
  if (isSimpleOrPepsCheck(check)) return true;
  if (hasSessionId(check)) return true;

  return hasSubcollection(check.__ref, EXPERIAN_SUBCOLLECTION_NAMES);
};

const getTypeClass = (type = "") => {
  const value = type.toLowerCase();

  if (value.includes("simple")) return "simple";
  if (value.includes("pep")) return "pep";
  if (value.includes("standard")) return "standard";
  if (value.includes("enhanced")) return "enhanced";
  if (value.includes("business")) return "business";

  return "default";
};

const buildSummaryFromChecks = (checks) => {
  const usersMap = new Map();

  checks.forEach((check) => {
    const email = getSummaryEmail(check) || "Unknown";
    const checkType = check.__checkTypeNorm || "UNKNOWN";
    const price = Number(check.__price || 0);

    if (!usersMap.has(email)) {
      usersMap.set(email, {
        name: nameFromEmail(email),
        email,
        totalCount: 0,
        totalAmount: 0,
        counts: {},
        money: {},
      });
    }

    const user = usersMap.get(email);

    user.totalCount += 1;
    user.totalAmount += price;
    user.counts[checkType] = (user.counts[checkType] || 0) + 1;
    user.money[checkType] = (user.money[checkType] || 0) + price;
  });

  return Array.from(usersMap.values());
};

// Shared monthly records for the Finance charges summary. This uses the same
// collection-group query, inclusion rules and pricing as the AML dashboard.
const archiveChargeCache = new Map();
export const loadAMLArchiveOnce = (month) => {
  if (!archiveChargeCache.has(month)) {
    archiveChargeCache.set(month, fetchAMLMonthlyChargeRecords(month).catch((error) => {
      archiveChargeCache.delete(month);
      throw error;
    }));
  }
  return archiveChargeCache.get(month);
};

export const subscribeAMLChargeRecords = (startMonth, onData, onError) => {
  let version = 0;
  const { start, end } = getMonthBounds(startMonth);
  const unsubscribe = onSnapshot(query(collectionGroup(db, "Checks"),
    where("createdAt", ">=", start.toISOString()),
    where("createdAt", "<", end.toISOString()),
    orderBy("createdAt", "asc")), async (snapshot) => {
      const current = ++version;
      try {
        const records = await mapAMLChargeSnapshot(snapshot);
        if (current === version) onData(records);
      } catch (error) { if (current === version) onError(error); }
    }, onError);
  return () => { version += 1; unsubscribe(); };
};

export const fetchAMLMonthlyChargeRecords = async (selectedMonth) => {
  const currentMonth = new Date();
  const selectedDate = new Date(`${selectedMonth}-01T00:00:00`);
  const liveCutoff = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
  if (selectedDate < liveCutoff) {
    const archiveUrl = await getDownloadURL(ref(storage, `check2/statements/${selectedMonth}/check2-monthly-statement-${selectedMonth}.xlsx`));
    const response = await fetch(archiveUrl);
    // if (!response.ok) throw new Error("AML archive could not be downloaded.");
    const workbook = XLSX.read(await response.arrayBuffer(), { type: "array" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    return XLSX.utils.sheet_to_json(sheet, { defval: "" }).map((row, index) => {
      const dateParts = String(row.Date || "").split("/");
      const date = dateParts.length === 3 ? `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}` : `${selectedMonth}-01`;
      const amount = Number(row.Price || 0);
      return { id: `archived-${selectedMonth}-${index}`, adviser: row.Name || row["User Email"] || "", adviserEmail: row["Requested For"] || row["Requested By"] || row["User Email"] || "", client: row["Client Name"] || row.Client || row["Applicant Name"] || "", chargeType: "AML Checks", description: row["Check Type"] || "", date, amountCharged: amount, expectedAmount: amount, sourcePath: "/AMLStatements" };
    });
  }
  const { start, end } = getMonthBounds(selectedMonth);
  const snapshot = await getDocs(query(
    collectionGroup(db, "Checks"),
    where("createdAt", ">=", start.toISOString()),
    where("createdAt", "<", end.toISOString()),
    orderBy("createdAt", "asc"),
  ));
  return mapAMLChargeSnapshot(snapshot);
};

const mapAMLChargeSnapshot = async (snapshot) => {
  const rawChecks = snapshot.docs.map((docSnap) => {
    const data = docSnap.data() || {};
    const checkType = normaliseCheckType(data.checkType);
    const expectedAmount = getCheckPrice(checkType);
    return {
      ...data,
      id: docSnap.ref.path,
      adviserEmail: getForAdviserEmailOnly(data) || docSnap.ref.parent?.parent?.id || "",
      adviser: nameFromEmail(getForAdviserEmailOnly(data) || docSnap.ref.parent?.parent?.id || ""),
      chargeType: "AML Checks",
      description: checkType,
      date: data.createdAt?.toDate?.()?.toISOString?.() || data.createdAt,
      amountCharged: Number.isFinite(Number(data.amountCharged)) ? Number(data.amountCharged) : expectedAmount,
      expectedAmount,
      sourcePath: "/AMLStatements",
      __ownerEmail: docSnap.ref.parent?.parent?.id || "",
      __ref: docSnap.ref,
      __checkTypeNorm: checkType,
      __price: expectedAmount,
    };
  });
  const included = await runInBatches(
    rawChecks,
    SUBCOLLECTION_BATCH_SIZE,
    async (check) => {
      if (!(await shouldIncludeCheck(check))) return null;
      const requestData = await getRequestDataForCheck(check.__ref);
      return { ...check, client: getAMLClientName(check, requestData) };
    },
  );
  return included.filter(Boolean);
};

const getSortValue = (row, key) => {
  if (key === "adviser") return row.name || row.email || "";
  if (key === "totalCount") return Number(row.totalCount || 0);
  if (key === "totalAmount") return Number(row.totalAmount || 0);

  if (key.startsWith("checkTypeCount:")) {
    const type = key.replace("checkTypeCount:", "");
    return Number(row.counts?.[type] || 0);
  }

  if (key.startsWith("checkTypeAmount:")) {
    const type = key.replace("checkTypeAmount:", "");
    return Number(row.money?.[type] || 0);
  }

  return "";
};

const sortSummaryRows = (rows = [], sortConfig) => {
  const { key, direction } = sortConfig;

  return [...rows].sort((a, b) => {
    const aValue = getSortValue(a, key);
    const bValue = getSortValue(b, key);

    if (typeof aValue === "number" && typeof bValue === "number") {
      return direction === "asc" ? aValue - bValue : bValue - aValue;
    }

    const result = String(aValue).localeCompare(String(bValue), undefined, {
      numeric: true,
      sensitivity: "base",
    });

    return direction === "asc" ? result : -result;
  });
};

const SortableHeader = ({ label, sortKey, sortConfig, onSort }) => {
  const isActive = sortConfig.key === sortKey;

  return (
    <button
      type="button"
      className={`aml-table-header-button ${
        isActive ? "aml-table-header-button--active" : ""
      }`}
      onClick={() => onSort(sortKey)}
    >
      <span>{label}</span>

      <span
        className={`aml-table-sort-icon ${
          isActive && sortConfig.direction === "desc"
            ? "aml-table-sort-icon--desc"
            : ""
        }`}
      >
        <SortIcon />
      </span>
    </button>
  );
};

const AMLSelectField = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select option",
}) => {
  const customOptions = useMemo(() => {
    return options
      .map((option) => ({
        id: option.value ?? option.id ?? "",
        name: option.label ?? option.name ?? "",
      }))
      .filter((option) => option.id || option.name);
  }, [options]);

  return (
    <div className="aml-filter-field">
      <span>{label}</span>

      <CustomSelect
        options={customOptions}
        value={value || ""}
        placeholder={placeholder}
        clearable={false}
        searchable
        searchPlaceholder="Type to filter..."
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
};

const CHECK_TYPE_ORDER = ["ENHANCED", "STANDARD", "SIMPLE", "PEP & SANCTIONS"];

const sortCheckTypes = (types = []) => {
  return [...types].sort((a, b) => {
    const aIndex = CHECK_TYPE_ORDER.findIndex(
      (type) => type.toLowerCase() === String(a).toLowerCase(),
    );

    const bIndex = CHECK_TYPE_ORDER.findIndex(
      (type) => type.toLowerCase() === String(b).toLowerCase(),
    );

    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    return String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });
};

export default function AMLMonthlyDashboard() {
  const navigate = useNavigate();

  const months = useMemo(() => getLastNMonths(), []);

  const [selectedMonth, setSelectedMonth] = useState(months[0]?.value || "");
  const [includedChecks, setIncludedChecks] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState("");

  const [selectedAdviser, setSelectedAdviser] = useState("all");
  const [selectedCheckType, setSelectedCheckType] = useState("all");
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT_CONFIG);

  const userPermissions = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userPermissions")) || [];
    } catch {
      return [];
    }
  }, []);

  const selectedMonthDetails = months.find(
    (month) => month.value === selectedMonth,
  );

  useEffect(() => {
    if (!userPermissions.includes("Finance")) {
      navigate("/");
    }
  }, [navigate, userPermissions]);

  useEffect(() => {
    setSelectedAdviser("all");
    setSelectedCheckType("all");
    setSortConfig(DEFAULT_SORT_CONFIG);
  }, [selectedMonth]);

  useEffect(() => {
    let cancelled = false;

    const fetchMonthlySummary = async () => {
      if (!selectedMonth) return;

      try {
        setLoadingSummary(true);
        setSummaryError("");

        const { start, end } = getMonthBounds(selectedMonth);

        const monthlyQuery = query(
          collectionGroup(db, "Checks"),
          where("createdAt", ">=", start.toISOString()),
          where("createdAt", "<", end.toISOString()),
          orderBy("createdAt", "asc"),
        );

        const snapshot = await getDocs(monthlyQuery);

        const rawChecks = snapshot.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          const ownerEmail = docSnap.ref.parent?.parent?.id || "";
          const checkType = normaliseCheckType(data.checkType);
          const price = getCheckPrice(checkType);

          return {
            ...data,
            __ownerEmail: ownerEmail,
            __ref: docSnap.ref,
            __summaryEmail: getForAdviserEmailOnly(data) || ownerEmail,
            __checkTypeNorm: checkType,
            __price: price,
          };
        });

        const includeResults = await runInBatches(
          rawChecks,
          SUBCOLLECTION_BATCH_SIZE,
          async (check) => {
            const include = await shouldIncludeCheck(check);
            return include ? check : null;
          },
        );

        if (!cancelled) {
          setIncludedChecks(includeResults.filter(Boolean));
        }
      } catch (error) {
        console.error("Failed to load AML monthly summary:", error);

        if (!cancelled) {
          setIncludedChecks([]);
          setSummaryError(
            error?.message?.includes("index")
              ? "Firestore needs a collection-group index on Checks.createdAt."
              : "Failed to load monthly summary.",
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingSummary(false);
        }
      }
    };

    fetchMonthlySummary();

    return () => {
      cancelled = true;
    };
  }, [selectedMonth]);

  const adviserOptions = useMemo(() => {
    return Array.from(
      new Set(
        includedChecks.map((check) => getSummaryEmail(check)).filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b));
  }, [includedChecks]);

 const checkTypeOptions = useMemo(() => {
   const types = Array.from(
     new Set(
       includedChecks
         .map((check) => check.__checkTypeNorm || "UNKNOWN")
         .filter(Boolean),
     ),
   );

   return sortCheckTypes(types);
 }, [includedChecks]);
  
  const adviserSelectOptions = useMemo(() => {
    return [
      {
        value: "all",
        label: "All advisers",
      },
      ...adviserOptions.map((email) => ({
        value: email,
        label: `${email}`,
      })),
    ];
  }, [adviserOptions]);

  const checkTypeSelectOptions = useMemo(() => {
    return [
      {
        value: "all",
        label: "All check types",
      },
      ...checkTypeOptions.map((type) => ({
        value: type,
        label: type,
      })),
    ];
  }, [checkTypeOptions]);

  const filteredChecks = useMemo(() => {
    return includedChecks.filter((check) => {
      const email = getSummaryEmail(check);
      const type = check.__checkTypeNorm || "UNKNOWN";

      if (selectedAdviser !== "all" && email !== selectedAdviser) {
        return false;
      }

      if (selectedCheckType !== "all" && type !== selectedCheckType) {
        return false;
      }

      return true;
    });
  }, [includedChecks, selectedAdviser, selectedCheckType]);

  const summaryRows = useMemo(() => {
    return buildSummaryFromChecks(filteredChecks);
  }, [filteredChecks]);

  const checkTypes = useMemo(() => {
    const types = Array.from(
      new Set(
        filteredChecks
          .map((check) => check.__checkTypeNorm || "UNKNOWN")
          .filter(Boolean),
      ),
    );

    return sortCheckTypes(types);
  }, [filteredChecks]);

  const sortedSummaryRows = useMemo(() => {
    return sortSummaryRows(summaryRows, sortConfig);
  }, [summaryRows, sortConfig]);

  const totals = useMemo(() => {
    return summaryRows.reduce(
      (acc, row) => {
        acc.users += 1;
        acc.checks += row.totalCount || 0;
        acc.amount += row.totalAmount || 0;
        return acc;
      },
      {
        users: 0,
        checks: 0,
        amount: 0,
      },
    );
  }, [summaryRows]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) {
        return {
          key,
          direction: "asc",
        };
      }

      if (prev.direction === "asc") {
        return {
          key,
          direction: "desc",
        };
      }

      return DEFAULT_SORT_CONFIG;
    });
  };

  const resetSort = () => {
    setSortConfig(DEFAULT_SORT_CONFIG);
  };

  return (
    <div className="Main-Page">
      <Helmet>
        <title>check² Statements</title>
        <meta
          name="description"
          content="Download monthly check² finance statements."
        />
      </Helmet>

      <Sidebar />

      <div className="Main-Container">
        <main className="aml-statements-dashboard">
          <Breadcrumb
            items={[
              {
                label: "Finance",
                to: "/Department/Finance",
              },
              { label: "check² Statements" },
            ]}
          />
          <section className="aml-statements-hero aml-statements-hero-dark">
            <div className="aml-statements-hero-content">
              <span className="aml-statements-eyebrow">Finance</span>

              <h1>check² Monthly Statements</h1>

              {/* <p>
                Review check² usage within the 90-day retention period and
                download monthly finance statements.
              </p> */}

              <div className="aml-hero-month-pills">
                {months.map((month) => {
                  const isActive = selectedMonth === month.value;

                  return (
                    <button
                      key={month.value}
                      type="button"
                      className={`aml-hero-month-pill${isActive ? " active" : ""}`}
                      onClick={() => setSelectedMonth(month.value)}
                      aria-pressed={isActive}
                    >
                      <span>{month.shortLabel}</span>

                      {month.isCurrentMonth && <small>Current</small>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="aml-statements-hero-panel">
              <span>Selected Month</span>

              <strong>
                {selectedMonthDetails?.label || "No month selected"}
              </strong>

              <small>{formatGBP(totals.amount)} filtered total</small>

              <div className="aml-hero-download-action">
                <DownloadAllUsersMonthlyStatement month={selectedMonth} />
              </div>
            </div>
          </section>
          {/* <section className="aml-statements-card">
            <div className="aml-statements-card-header">
              <div>
                <span className="aml-section-label">Statement Period</span>
                <h2>Choose a month</h2>
                <p>
                  Only the most recent 90 days are available because older
                  checks are deleted automatically.
                </p>
              </div>
            </div>

            <div className="aml-month-grid">
              {months.map((month) => {
                const isActive = selectedMonth === month.value;

                return (
                  <button
                    key={month.value}
                    type="button"
                    className={`aml-month-card${isActive ? " active" : ""}`}
                    onClick={() => setSelectedMonth(month.value)}
                    aria-pressed={isActive}
                  >
                    <span>{month.shortLabel}</span>
                    <strong>{month.label}</strong>

                    {month.isCurrentMonth && (
                      <small className="aml-current-pill">Current</small>
                    )}
                  </button>
                );
              })}
            </div>
          </section> */}

          <section className="aml-statements-card">
            <div className="aml-statements-card-header">
              <div>
                <span className="aml-section-label">Filters</span>
                <h2>Filter statement view</h2>
                <p>
                  Filter this month by adviser and check type. The Excel
                  download still exports the selected month.
                </p>
              </div>
            </div>

            <div className="aml-filter-grid">
              <AMLSelectField
                label="Adviser"
                value={selectedAdviser}
                options={adviserSelectOptions}
                placeholder="All advisers"
                onChange={setSelectedAdviser}
              />

              <AMLSelectField
                label="Check Type"
                value={selectedCheckType}
                options={checkTypeSelectOptions}
                placeholder="All check types"
                onChange={setSelectedCheckType}
              />
            </div>

            {checkTypeOptions.length > 0 && (
              <div className="aml-check-type-legend">
                {checkTypeOptions.map((type) => (
                  <span
                    key={type}
                    className={`aml-check-type-pill aml-check-${getTypeClass(
                      type,
                    )}`}
                  >
                    {type}
                  </span>
                ))}
              </div>
            )}
          </section>

          <section className="aml-summary-grid">
            <div className="aml-summary-card aml-summary-card-blue">
              <span>Users</span>
              <strong>{totals.users}</strong>
              <p>Filtered users with checks</p>
            </div>

            <div className="aml-summary-card aml-summary-card-cream">
              <span>Checks</span>
              <strong>{totals.checks}</strong>
              <p>Filtered included checks</p>
            </div>

            <div className="aml-summary-card aml-summary-card-green">
              <span>Total</span>
              <strong>{formatGBP(totals.amount)}</strong>
              <p>Filtered statement value</p>
            </div>
          </section>

          <section className="aml-statements-card">
            <div className="aml-statements-card-header">
              <div>
                <span className="aml-section-label">User Summary</span>
                <h2>Statement Breakdown by User</h2>
                <p>
                  Cards are grouped by requested-for adviser where available,
                  otherwise by the user who requested the check.
                </p>
              </div>

              <button
                type="button"
                className="aml-reset-sort-button"
                onClick={resetSort}
              >
                Reset Sort
              </button>
            </div>

            {summaryError && (
              <div className="aml-summary-error">{summaryError}</div>
            )}

            {loadingSummary ? (
              <div className="aml-summary-loading">Loading user summary...</div>
            ) : summaryRows.length === 0 ? (
              <div className="aml-summary-empty">
                No checks found for this filter.
              </div>
            ) : (
              <div className="aml-user-summary-table-wrap">
                <table className="aml-user-summary-table">
                  <thead>
                    <tr>
                      <th>
                        <SortableHeader
                          label="Adviser"
                          sortKey="adviser"
                          sortConfig={sortConfig}
                          onSort={handleSort}
                        />
                      </th>

                      <th>
                        <SortableHeader
                          label="Total Checks"
                          sortKey="totalCount"
                          sortConfig={sortConfig}
                          onSort={handleSort}
                        />
                      </th>

                      <th>
                        <SortableHeader
                          label="Total Cost"
                          sortKey="totalAmount"
                          sortConfig={sortConfig}
                          onSort={handleSort}
                        />
                      </th>

                      {checkTypes.map((type) => (
                        <th key={type}>
                          <SortableHeader
                            label={type}
                            sortKey={`checkTypeCount:${type}`}
                            sortConfig={sortConfig}
                            onSort={handleSort}
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {sortedSummaryRows.map((row) => (
                      <tr key={row.email}>
                        <td>
                          <div className="aml-table-adviser">
                            <strong>{row.name || "Unknown User"}</strong>
                            <span>{row.email}</span>
                          </div>
                        </td>

                        <td>
                          <strong>{row.totalCount}</strong>
                        </td>

                        <td>
                          <strong>{formatGBP(row.totalAmount)}</strong>
                        </td>

                        {checkTypes.map((type) => {
                          const count = row.counts[type] || 0;
                          const amount = row.money[type] || 0;
                          const hasValue = count > 0 || amount > 0;

                          return (
                            <td key={`${row.email}-${type}`}>
                              <div
                                className={`aml-table-check-pill ${
                                  hasValue
                                    ? `aml-check-${getTypeClass(type)}`
                                    : "aml-check-empty"
                                }`}
                              >
                                <div>
                                  <strong>{type}</strong>
                                  <span>
                                    {count} check{count !== 1 ? "s" : ""}
                                  </span>
                                </div>

                                <b>{formatGBP(amount)}</b>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}
